// Custom Feedback Forms — Admin Module (iter165)
// Tabs: Overview · Categories & Areas · Form Builder · Submissions · Settings
import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Trash2, RefreshCw, Download, QrCode, AlertTriangle, Sparkles, ChevronRight, Printer, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Link, useSearchParams } from 'react-router-dom';
import { buildFeedbackPublicUrl, isValidFeedbackUrl } from '../../lib/cxUrls';
import CxAnalytics from './CxAnalytics';
import CxReputation from './CxReputation';
import CxDateRangeFilter, { loadSavedRange, resolveRange } from './CxDateRangeFilter';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const QUESTION_TYPES = [
  { v: 'nps',             label: 'NPS Score (0–10)' },
  { v: 'rating',          label: 'Rating (1–5 stars)' },
  { v: 'emoji',           label: 'Emoji Scale' },
  { v: 'yes_no',          label: 'Yes / No' },
  { v: 'multiple_choice', label: 'Multiple Choice (pick one)' },
  { v: 'multi_select',    label: 'Checkbox (pick many)' },
  { v: 'short_text',      label: 'Short Text' },
  { v: 'long_text',       label: 'Long Text' },
];

export default function CxAdmin() {
  const { getHeaders } = useAuth();
  const headers = useMemo(() => getHeaders(), [getHeaders]);
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'overview';
  const [tab, setTab] = useState(initialTab);

  // Reflect ?tab=reputation deep-links (e.g. from OAuth callback).
  useEffect(() => {
    const next = searchParams.get('tab');
    if (next && next !== tab) setTab(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-[#FAFBF8] p-4 lg:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-5">
          <h1 className="text-2xl font-semibold text-[#1A2421] tracking-tight">Customer Pulse Index</h1>
          <p className="text-[12.5px] text-[#5C6B64] mt-0.5">
            Hospitality experience engine · QR-driven, area-aware guest feedback.
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-[#E2E8DF] mb-5">
            <TabsTrigger value="overview"    data-testid="cx-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="analytics"   data-testid="cx-tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="reputation"  data-testid="cx-tab-reputation">Reputation</TabsTrigger>
            <TabsTrigger value="structure"   data-testid="cx-tab-structure">Categories & Areas</TabsTrigger>
            <TabsTrigger value="forms"       data-testid="cx-tab-forms">Form Builder</TabsTrigger>
            <TabsTrigger value="submissions" data-testid="cx-tab-submissions">Submissions</TabsTrigger>
            <TabsTrigger value="settings"    data-testid="cx-tab-settings">Settings</TabsTrigger>
          </TabsList>
          <TabsContent value="overview"><OverviewTab headers={headers} /></TabsContent>
          <TabsContent value="analytics"><CxAnalytics headers={headers} /></TabsContent>
          <TabsContent value="reputation"><CxReputation headers={headers} /></TabsContent>
          <TabsContent value="structure"><StructureTab headers={headers} /></TabsContent>
          <TabsContent value="forms"><FormsTab headers={headers} /></TabsContent>
          <TabsContent value="submissions"><SubmissionsTab headers={headers} /></TabsContent>
          <TabsContent value="settings"><SettingsTab headers={headers} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

/* ────────────────── OVERVIEW (Executive Command Center) ────────────────── */
//
// iter172 — Premium operational intelligence dashboard. Ranking is canonical
// (computed server-side via `/cx/analytics/executive`) — frontend renders
// only. Color tokens map 1:1 to backend `severity` field:
//   excellent / good → green   caution → amber   urgent → red.
//
const SEVERITY_TOKENS = {
  excellent: { dot: '#2D6A4F', bg: 'bg-[#EEF4EF]', border: 'border-[#2D6A4F]/25', text: 'text-[#2D6A4F]', label: 'Excellent' },
  good:      { dot: '#52A57B', bg: 'bg-[#F1F7F2]', border: 'border-[#52A57B]/25', text: 'text-[#2D6A4F]', label: 'Healthy' },
  caution:   { dot: '#D89B5B', bg: 'bg-[#FEF6EC]', border: 'border-[#D89B5B]/30', text: 'text-[#9C6815]', label: 'Caution' },
  urgent:    { dot: '#C45A41', bg: 'bg-[#FCEDE7]', border: 'border-[#C45A41]/30', text: 'text-[#C45A41]', label: 'Urgent' },
  unknown:   { dot: '#9AAA9F', bg: 'bg-[#F9F9F8]', border: 'border-[#E2E8DF]',     text: 'text-[#5C6B64]', label: '—' },
};

const npsKpiColor = (n) =>
  n == null ? '#5C6B64' : n >= 60 ? '#2D6A4F' : n >= 30 ? '#52A57B'
    : n >= 0 ? '#D89B5B' : '#C45A41';

const ratingKpiColor = (r) =>
  r == null ? '#5C6B64' : r >= 4.5 ? '#2D6A4F' : r >= 4 ? '#52A57B'
    : r >= 3 ? '#D89B5B' : '#C45A41';

function OverviewTab({ headers }) {
  const [exec, setExec] = useState(null);
  const [drill, setDrill] = useState(null); // {area_id}
  // iter176 — shared date range with Analytics + Submissions via localStorage.
  const [dateRange, setDateRange] = useState(() => {
    const saved = loadSavedRange();
    return { ...saved, resolved: resolveRange(saved.preset, saved.custom) };
  });

  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    p.set('start_date', dateRange.resolved.start_date);
    p.set('end_date', dateRange.resolved.end_date);
    p.set('days', String(dateRange.resolved.days));
    return p.toString();
  }, [dateRange]);

  useEffect(() => {
    axios.get(`${API}/cx/analytics/executive?${queryParams}`, { headers })
      .then((r) => setExec(r.data))
      .catch(() => {});
  }, [headers, queryParams]);

  if (!exec) return <div className="text-[#5C6B64] py-12 text-center">Loading guest-experience intelligence…</div>;

  const k = exec.kpis;
  const npsTooltip = k.nps_source === 'derived'
    ? 'NPS derived from rating questions (no explicit NPS in form)'
    : k.nps_source === 'mixed'
      ? 'NPS combines explicit NPS answers + derived from ratings'
      : k.nps_source === 'none' ? 'No NPS or rating data yet' : 'NPS from explicit responses';

  return (
    <div className="space-y-6" data-testid="cx-overview">
      {/* ─── Header strip — context line ─── */}
      <div className="flex items-baseline justify-between flex-wrap gap-2 -mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C6B64] font-medium">
            {dateRange.resolved.start_date} → {dateRange.resolved.end_date}
          </div>
          <h2 className="text-xl font-semibold text-[#1A2421] mt-0.5">Guest Experience · Command Center</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <CxDateRangeFilter value={dateRange} onChange={setDateRange} testId="cx-overview-daterange" />
          {k.alerts_open > 0 && (
            <div className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#C45A41] bg-[#FCEDE7] border border-[#C45A41]/30 rounded-full px-3 py-1">
              <AlertTriangle className="w-3.5 h-3.5" /> {k.alerts_open} open alert{k.alerts_open === 1 ? '' : 's'}
            </div>
          )}
          <Button variant="outline" size="sm" onClick={async () => {
            try {
              const r = await axios.get(`${API}/cx/analytics/export.xlsx?${queryParams}`,
                { headers, responseType: 'blob' });
              const url = URL.createObjectURL(new Blob([r.data]));
              const a = document.createElement('a');
              a.href = url;
              a.download = `cx-analytics-${new Date().toISOString().slice(0, 10)}.xlsx`;
              a.click();
              URL.revokeObjectURL(url);
              toast.success('Excel report downloaded');
            } catch (e) { toast.error('Export failed'); }
          }} data-testid="cx-export-xlsx">
            <Download className="w-3.5 h-3.5 mr-1" /> Export Excel
          </Button>
          <Button variant="outline" size="sm" onClick={async () => {
            try {
              const r = await axios.get(`${API}/cx/analytics/export.pdf?${queryParams}`,
                { headers, responseType: 'blob' });
              const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
              const a = document.createElement('a');
              a.href = url;
              a.download = `cx-experience-report-${new Date().toISOString().slice(0, 10)}.pdf`;
              a.click();
              URL.revokeObjectURL(url);
              toast.success('Hospitality report (PDF) downloaded');
            } catch (e) { toast.error('PDF export failed'); }
          }} data-testid="cx-export-pdf">
            <Download className="w-3.5 h-3.5 mr-1" /> Export PDF
          </Button>
        </div>
      </div>

      {/* ─── Executive KPI row (5 cards) ─── */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard label="Net Promoter Score"
                 value={k.nps != null ? k.nps : '—'}
                 sub={`${exec.sentiment_split.positive + exec.sentiment_split.neutral + exec.sentiment_split.negative} responses · ${k.nps_source}`}
                 color={npsKpiColor(k.nps)} tooltip={npsTooltip} testId="cx-kpi-nps" />
        <KpiCard label="Avg Rating"
                 value={k.avg_rating != null ? k.avg_rating.toFixed(2) : '—'}
                 sub="out of 5.0"
                 color={ratingKpiColor(k.avg_rating)} testId="cx-kpi-rating" />
        <KpiCard label="Positive Sentiment"
                 value={k.positive_pct != null ? `${k.positive_pct}%` : '—'}
                 sub={`${exec.sentiment_split.positive} of ${k.submission_count} responses`}
                 color={k.positive_pct >= 60 ? '#2D6A4F' : k.positive_pct >= 40 ? '#D89B5B' : '#C45A41'}
                 testId="cx-kpi-positive" />
        <KpiCard label="Total Responses"
                 value={k.submission_count}
                 sub="last 30 days"
                 testId="cx-kpi-subs" />
        <KpiCard label="Open Alerts"
                 value={k.alerts_open}
                 sub={k.alerts_open === 0 ? 'All clear ✓' : 'Action required'}
                 color={k.alerts_open === 0 ? '#2D6A4F' : '#C45A41'}
                 testId="cx-kpi-alerts" />
      </div>

      {/* ─── Best / Worst summary strip ─── */}
      <div className="grid md:grid-cols-3 gap-3">
        <SummaryStripCard label="Best Performing"
          icon={<Sparkles className="w-4 h-4 text-[#2D6A4F]" />}
          area={exec.best_area} severityKey="excellent"
          onClick={(id) => setDrill({ area_id: id })} testId="cx-best-strip" />
        <SummaryStripCard label="Worst Performing"
          icon={<AlertTriangle className="w-4 h-4 text-[#C45A41]" />}
          area={exec.worst_area} severityKey="urgent"
          onClick={(id) => setDrill({ area_id: id })} testId="cx-worst-strip" />
        <SummaryStripCard label="Most Complained"
          icon={<AlertTriangle className="w-4 h-4 text-[#D89B5B]" />}
          area={exec.most_complained_area} severityKey="caution"
          onClick={(id) => setDrill({ area_id: id })} testId="cx-most-complained-strip" />
      </div>

      {/* ─── Sentiment Split ─── */}
      <Card className="border-[#E2E8DF]">
        <CardHeader className="pb-2"><CardTitle className="text-[15px]">Sentiment Split</CardTitle></CardHeader>
        <CardContent>
          <SentimentBar split={exec.sentiment_split} />
          <div className="grid grid-cols-3 gap-3 mt-3 text-[12px]">
            <SentimentLegend label="Positive" count={exec.sentiment_split.positive}
                             total={k.submission_count} color="#2D6A4F" />
            <SentimentLegend label="Neutral" count={exec.sentiment_split.neutral}
                             total={k.submission_count} color="#D89B5B" />
            <SentimentLegend label="Negative" count={exec.sentiment_split.negative}
                             total={k.submission_count} color="#C45A41" />
          </div>
        </CardContent>
      </Card>

      {/* ─── Best Performing Areas grid ─── */}
      <div>
        <div className="flex items-baseline gap-2 mb-2">
          <h3 className="text-[14px] font-semibold text-[#1A2421]">Best Performing Areas</h3>
          <span className="text-[11px] text-[#5C6B64]">ranked by NPS · click to drill down</span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="cx-best-areas">
          {exec.best_areas.length === 0 && (
            <div className="col-span-full text-[12.5px] text-[#5C6B64] bg-[#F9F9F8] border border-dashed border-[#E2E8DF] rounded-md p-4 text-center">
              No ranked areas yet — needs at least one rating or NPS submission.
            </div>
          )}
          {exec.best_areas.map((a) => (
            <AreaPerformanceCard key={a.area_id} area={a}
              onClick={() => setDrill({ area_id: a.area_id })}
              testId={`cx-best-area-${a.area_id}`} />
          ))}
        </div>
      </div>

      {/* ─── Needs Attention grid ─── */}
      <div>
        <div className="flex items-baseline gap-2 mb-2">
          <h3 className="text-[14px] font-semibold text-[#1A2421]">Needs Attention</h3>
          <span className="text-[11px] text-[#5C6B64]">caution + urgent areas · sorted by severity</span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="cx-needs-attention">
          {exec.needs_attention.length === 0 && (
            <div className="col-span-full text-[12.5px] text-[#5C6B64] bg-[#F1F7F2] border border-[#2D6A4F]/25 rounded-md p-4 text-center">
              ✓ All clear. No area currently needs attention.
            </div>
          )}
          {exec.needs_attention.map((a) => (
            <AreaPerformanceCard key={a.area_id} area={a} attentionMode
              onClick={() => setDrill({ area_id: a.area_id })}
              testId={`cx-attention-area-${a.area_id}`} />
          ))}
        </div>
      </div>

      {/* Drilldown modal */}
      {drill && (
        <AreaDrilldownDialog
          areaId={drill.area_id}
          headers={headers}
          onClose={() => setDrill(null)} />
      )}
    </div>
  );
}

// ─── KPI card with optional tooltip + color band on top ──────────────
function KpiCard({ label, value, sub, color, testId, tooltip }) {
  return (
    <Card className="border-[#E2E8DF] relative overflow-hidden" data-testid={testId}>
      {color && (
        <div className="absolute top-0 left-0 right-0 h-1" style={{ background: color }} />
      )}
      <CardContent className="p-4">
        <div className="text-[10px] uppercase tracking-[0.12em] text-[#5C6B64] font-medium flex items-center gap-1"
             title={tooltip || undefined}>
          {label}
          {tooltip && <span className="text-[10px] cursor-help">ⓘ</span>}
        </div>
        <div className="text-[28px] sm:text-[30px] font-semibold mt-1.5 tabular-nums leading-tight"
             style={{ color: color || '#1A2421' }}>{value}</div>
        {sub && <div className="text-[11px] text-[#5C6B64] mt-1 capitalize">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function SummaryStripCard({ label, icon, area, severityKey, onClick, testId }) {
  const tok = SEVERITY_TOKENS[severityKey] || SEVERITY_TOKENS.unknown;
  if (!area) {
    return (
      <Card className="border-[#E2E8DF] bg-[#F9F9F8]" data-testid={testId}>
        <CardContent className="p-4">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-[#5C6B64] font-medium">
            {icon} {label}
          </div>
          <div className="text-[14px] text-[#5C6B64] mt-2">—</div>
          <div className="text-[10px] text-[#5C6B64] mt-1">No data yet</div>
        </CardContent>
      </Card>
    );
  }
  return (
    <button
      type="button" onClick={() => onClick && onClick(area.area_id)}
      className={`w-full text-left rounded-lg border ${tok.border} ${tok.bg} p-4 hover:shadow-sm transition-all hover:-translate-y-px`}
      data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-[#5C6B64] font-medium">
        {icon} {label}
      </div>
      <div className="text-[18px] font-semibold text-[#1A2421] mt-1.5 leading-tight">{area.area_name}</div>
      <div className="flex items-center gap-3 mt-2 text-[12px] tabular-nums">
        {area.nps != null && <span className={tok.text + ' font-medium'}>NPS {area.nps}</span>}
        {area.avg_rating != null && <span className="text-[#5C6B64]">★ {area.avg_rating.toFixed(1)}</span>}
        {area.negative_count != null && <span className="text-[#5C6B64]">{area.negative_count} complaint{area.negative_count === 1 ? '' : 's'}</span>}
      </div>
    </button>
  );
}

function AreaPerformanceCard({ area, onClick, attentionMode, testId }) {
  const tok = SEVERITY_TOKENS[area.severity] || SEVERITY_TOKENS.unknown;
  const pos = area.sentiment_split?.positive || 0;
  const neg = area.sentiment_split?.negative || 0;
  const total = Math.max(1, area.submission_count || 0);
  const posPct = Math.round(100 * pos / total);
  return (
    <button
      type="button" onClick={onClick}
      className={`w-full text-left rounded-lg border ${tok.border} bg-white p-4 hover:shadow-md transition-all hover:-translate-y-px`}
      data-testid={testId}>
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.14em] text-[#5C6B64] font-medium truncate">
            {area.category_name || '—'}
          </div>
          <div className="text-[15px] font-semibold text-[#1A2421] mt-0.5 truncate">{area.area_name}</div>
        </div>
        <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${tok.text} ${tok.bg} px-2 py-0.5 rounded-full whitespace-nowrap`}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: tok.dot }} /> {tok.label}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3 pb-2 border-b border-[#F0F2EE]">
        <div>
          <div className="text-[9.5px] uppercase tracking-wider text-[#5C6B64]">NPS</div>
          <div className="text-[18px] font-semibold tabular-nums leading-none mt-0.5"
               style={{ color: npsKpiColor(area.nps) }}>
            {area.nps != null ? area.nps : '—'}
          </div>
        </div>
        <div>
          <div className="text-[9.5px] uppercase tracking-wider text-[#5C6B64]">Rating</div>
          <div className="text-[18px] font-semibold tabular-nums leading-none mt-0.5 text-[#1A2421]">
            {area.avg_rating != null ? area.avg_rating.toFixed(1) : '—'}
          </div>
        </div>
        <div>
          <div className="text-[9.5px] uppercase tracking-wider text-[#5C6B64]">Positive %</div>
          <div className="text-[18px] font-semibold tabular-nums leading-none mt-0.5 text-[#1A2421]">
            {posPct}%
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between mt-2 text-[11px] text-[#5C6B64]">
        <span>{area.submission_count} response{area.submission_count === 1 ? '' : 's'}</span>
        {attentionMode && neg > 0 ? (
          <span className="text-[#C45A41] font-medium">{neg} negative</span>
        ) : (
          <span className="text-[#5C6B64]">{area.nps_source === 'derived' ? 'derived' : 'explicit'} NPS</span>
        )}
      </div>
    </button>
  );
}

function SentimentLegend({ label, count, total, color }) {
  const pct = total > 0 ? Math.round(100 * count / total) : 0;
  return (
    <div className="flex items-center gap-2">
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
      <div className="flex-1">
        <div className="text-[11px] text-[#5C6B64]">{label}</div>
        <div className="text-[14px] font-semibold tabular-nums text-[#1A2421]">{count} <span className="text-[11px] text-[#5C6B64] font-normal">({pct}%)</span></div>
      </div>
    </div>
  );
}

function SentimentBar({ split }) {
  const total = Math.max(1, (split.positive || 0) + (split.neutral || 0) + (split.negative || 0));
  const p = (split.positive || 0) / total * 100;
  const n = (split.neutral  || 0) / total * 100;
  const g = (split.negative || 0) / total * 100;
  return (
    <div className="flex h-3 rounded-full overflow-hidden bg-[#F0F2EE]" data-testid="cx-sentiment-bar">
      <div style={{ width: `${p}%`, background: '#2D6A4F' }} />
      <div style={{ width: `${n}%`, background: '#D89B5B' }} />
      <div style={{ width: `${g}%`, background: '#C45A41' }} />
    </div>
  );
}

/* ─── Area Drilldown Dialog ─────────────────────────────────────────── */
function AreaDrilldownDialog({ areaId, headers, onClose }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    axios.get(`${API}/cx/analytics/area/${areaId}?days=90`, { headers })
      .then((r) => setDetail(r.data))
      .catch(() => setDetail({ error: 'Failed to load' }));
  }, [areaId, headers]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="cx-drilldown">
        {!detail && <div className="py-10 text-center text-[#5C6B64]">Loading drilldown…</div>}
        {detail && detail.error && <div className="py-10 text-center text-[#C45A41]">{detail.error}</div>}
        {detail && !detail.error && (
          <>
            <DialogHeader>
              <div className="text-[10px] uppercase tracking-[0.14em] text-[#5C6B64] font-medium">
                {detail.category?.name || 'Area drilldown'}
              </div>
              <DialogTitle className="text-[20px]">{detail.area?.name}</DialogTitle>
            </DialogHeader>

            {/* KPI row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
              <KpiCard label="NPS" value={detail.nps != null ? detail.nps : '—'}
                       sub={detail.nps_source} color={npsKpiColor(detail.nps)} />
              <KpiCard label="Avg Rating" value={detail.avg_rating != null ? detail.avg_rating.toFixed(2) : '—'}
                       sub="out of 5" color={ratingKpiColor(detail.avg_rating)} />
              <KpiCard label="Responses" value={detail.submission_count}
                       sub={`p ${detail.promoters} · ps ${detail.passives} · d ${detail.detractors}`} />
              <KpiCard label="Severity"
                       value={(SEVERITY_TOKENS[detail.severity] || SEVERITY_TOKENS.unknown).label}
                       color={(SEVERITY_TOKENS[detail.severity] || SEVERITY_TOKENS.unknown).dot} />
            </div>

            {/* Sentiment split */}
            <Card className="border-[#E2E8DF] mt-3">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-[0.12em] text-[#5C6B64] font-medium mb-2">Sentiment</div>
                <SentimentBar split={detail.sentiment_split} />
                <div className="grid grid-cols-3 gap-3 mt-2 text-[11px] text-[#5C6B64]">
                  <span>Positive {detail.sentiment_split.positive}</span>
                  <span>Neutral {detail.sentiment_split.neutral}</span>
                  <span>Negative {detail.sentiment_split.negative}</span>
                </div>
              </CardContent>
            </Card>

            {/* NPS history */}
            {detail.nps_history?.length > 0 && (
              <Card className="border-[#E2E8DF] mt-3">
                <CardHeader className="pb-2"><CardTitle className="text-[13px]">NPS Trend (90 days)</CardTitle></CardHeader>
                <CardContent>
                  <NpsTrendStrip points={detail.nps_history} />
                </CardContent>
              </Card>
            )}

            {/* iter173 — Question Performance Breakdown */}
            {detail.question_breakdown?.length > 0 && (
              <Card className="border-[#E2E8DF] mt-3">
                <CardHeader className="pb-2">
                  <CardTitle className="text-[13px]">Question Performance</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {detail.question_breakdown
                    .filter((q) => q.response_count > 0)
                    .map((q) => {
                      const tok = SEVERITY_TOKENS[q.severity] || SEVERITY_TOKENS.unknown;
                      const isText = q.type === 'short_text' || q.type === 'long_text';
                      return (
                        <details key={q.question_id} className="border border-[#E2E8DF] rounded-md group"
                                 data-testid={`cx-q-perf-${q.question_id}`}>
                          <summary className="px-3 py-2 cursor-pointer hover:bg-[#F9F9F8] list-none flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: tok.dot }} />
                            <div className="min-w-0 flex-1">
                              <div className="text-[12.5px] text-[#1A2421] font-medium truncate">{q.label}</div>
                              <div className="text-[10.5px] text-[#5C6B64]">{q.type} · {q.response_count} response{q.response_count === 1 ? '' : 's'}</div>
                            </div>
                            <div className="flex items-center gap-3 text-[11px] tabular-nums shrink-0">
                              {q.avg_rating != null && (
                                <span className="text-[#1A2421]">★ {q.avg_rating}</span>
                              )}
                              {q.nps != null && (
                                <span style={{ color: tok.dot }}>NPS {q.nps}{q.nps_source === 'derived' && <sup className="text-[8px]">†</sup>}</span>
                              )}
                              {q.negative_pct > 0 && (
                                <span className="text-[#C45A41]">{q.negative_pct}% neg</span>
                              )}
                            </div>
                            <span className={`text-[9.5px] font-medium ${tok.text} ${tok.bg} px-1.5 py-0.5 rounded-full whitespace-nowrap`}>
                              {tok.label}
                            </span>
                          </summary>
                          <div className="border-t border-[#E2E8DF] p-3 bg-[#FAFBF8] space-y-2">
                            {/* yes/no breakdown */}
                            {q.type === 'yes_no' && (
                              <div className="text-[11.5px] text-[#5C6B64]">
                                ✓ {q.yes_count} yes · ✗ {q.no_count} no
                              </div>
                            )}
                            {/* mcq/multi option counts */}
                            {q.option_counts?.length > 0 && (
                              <div className="flex flex-wrap gap-1.5">
                                {q.option_counts.map((o) => (
                                  <span key={o.option} className="text-[11px] bg-white border border-[#E2E8DF] rounded-full px-2 py-0.5 text-[#1A2421]">
                                    {o.option} <span className="opacity-60">×{o.count}</span>
                                  </span>
                                ))}
                              </div>
                            )}
                            {/* sample free-text comments */}
                            {isText && q.sample_comments?.length > 0 && (
                              <div className="space-y-1.5">
                                {q.sample_comments.map((c, i) => {
                                  const ctok = SEVERITY_TOKENS[c.sentiment === 'positive' ? 'excellent'
                                    : c.sentiment === 'negative' ? 'urgent' : 'caution'];
                                  return (
                                    <div key={i} className={`text-[11.5px] border-l-2 pl-2 py-1 ${ctok.border}`}>
                                      <span className="italic text-[#1A2421]">"{c.text}"</span>
                                      <span className="text-[10px] text-[#5C6B64] ml-1.5">— {c.guest_name}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            {!isText && q.sample_comments?.length === 0 && !q.option_counts?.length && q.type !== 'yes_no' && (
                              <div className="text-[11px] text-[#5C6B64]">Click outside to collapse.</div>
                            )}
                          </div>
                        </details>
                      );
                    })}
                  <div className="text-[10.5px] text-[#5C6B64] pt-1">
                    <sup>†</sup> NPS derived from rating when no explicit NPS answer exists.
                  </div>
                </CardContent>
              </Card>
            )}

            {/* iter173 — Top Complaint Themes (operational hospitality buckets) */}
            {detail.complaint_themes?.length > 0 && (
              <Card className="border-[#E2E8DF] mt-3">
                <CardHeader className="pb-2"><CardTitle className="text-[13px]">Most Mentioned Complaints</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {detail.complaint_themes.map((t) => (
                    <div key={t.theme} className="border border-[#FCEDE7] bg-[#FFFBF8] rounded-md p-2.5"
                         data-testid={`cx-theme-${t.theme}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[12.5px] font-medium text-[#C45A41]">{t.theme}</span>
                        <span className="text-[11px] text-[#5C6B64] tabular-nums">{t.count} mention{t.count === 1 ? '' : 's'}</span>
                      </div>
                      {t.examples?.length > 0 && (
                        <div className="mt-1.5 space-y-1">
                          {t.examples.slice(0, 2).map((e, i) => (
                            <div key={i} className="text-[11px] text-[#5C6B64] italic">"{e.text}"</div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Recent guest comments */}
            {detail.recent_comments?.length > 0 && (
              <Card className="border-[#E2E8DF] mt-3">
                <CardHeader className="pb-2"><CardTitle className="text-[13px]">Recent Guest Comments</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {detail.recent_comments.map((c, i) => {
                    const tok = SEVERITY_TOKENS[c.sentiment === 'positive' ? 'excellent' : c.sentiment === 'negative' ? 'urgent' : 'caution'];
                    return (
                      <div key={i} className={`border-l-2 pl-3 py-1 ${tok.border}`} data-testid={`cx-comment-${i}`}>
                        <div className="text-[13px] text-[#1A2421] italic">"{c.comment}"</div>
                        <div className="text-[10.5px] text-[#5C6B64] mt-1 flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{c.guest_name}</span>
                          <span className={`${tok.text} ${tok.bg} px-1.5 py-0.5 rounded-full text-[9.5px] font-medium`}>
                            {tok.label}
                          </span>
                          <span>{(c.submitted_at || '').slice(0, 10)}</span>
                          {c.avg_rating != null && <span>★ {c.avg_rating}</span>}
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Top complaint keywords */}
            {detail.recurring_negative_keywords?.length > 0 && (
              <Card className="border-[#E2E8DF] mt-3">
                <CardHeader className="pb-2"><CardTitle className="text-[13px]">Top Complaint Themes</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1.5">
                    {detail.recurring_negative_keywords.map((k) => (
                      <span key={k.word} className="text-[11px] bg-[#FCEDE7] border border-[#C45A41]/25 text-[#C45A41] rounded-full px-2 py-0.5">
                        {k.word} <span className="opacity-60">×{k.count}</span>
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex justify-end pt-3">
              <Button variant="outline" onClick={onClose} data-testid="cx-drilldown-close">Close</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function NpsTrendStrip({ points }) {
  // Tiny inline sparkline-like trend bar visualisation.
  const vals = points.map((p) => p.avg_nps);
  const min = Math.min(...vals, 0);
  const max = Math.max(...vals, 10);
  const range = Math.max(1, max - min);
  return (
    <div className="flex items-end gap-0.5 h-12">
      {points.map((p, i) => {
        const h = ((p.avg_nps - min) / range) * 100;
        const color = p.avg_nps >= 7 ? '#2D6A4F' : p.avg_nps >= 5 ? '#D89B5B' : '#C45A41';
        return (
          <div key={i} className="flex-1 rounded-sm" title={`${p.date}: ${p.avg_nps}`}
            style={{ height: `${Math.max(4, h)}%`, background: color, minWidth: '4px' }} />
        );
      })}
    </div>
  );
}

/* ────────────────── STRUCTURE (Categories + Areas + QR) ────────────────── */
function StructureTab({ headers }) {
  const [cats, setCats] = useState([]);
  const [areasByCat, setAreasByCat] = useState({});
  const [forms, setForms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [creating, setCreating] = useState({ open: false, type: null, categoryId: null });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCat, setBulkCat] = useState(null);
  const [baseUrlCache, setBaseUrlCache] = useState('');

  const load = useCallback(async () => {
    const [c, a, f, t] = await Promise.all([
      axios.get(`${API}/cx/categories`, { headers }),
      axios.get(`${API}/cx/areas`, { headers }),
      axios.get(`${API}/cx/forms`, { headers }),
      axios.get(`${API}/cx/templates`, { headers }),
    ]);
    setCats(c.data); setForms(f.data); setTemplates(t.data);
    const grouped = {};
    a.data.forEach((ar) => { (grouped[ar.category_id] = grouped[ar.category_id] || []).push(ar); });
    setAreasByCat(grouped);
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const applyTemplate = async (id) => {
    try {
      const r = await axios.post(`${API}/cx/templates/${id}/apply`, {}, { headers });
      toast.success(`Template applied: +${r.data.created_categories} categories, +${r.data.created_areas} areas`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const createCategory = async (name) => {
    await axios.post(`${API}/cx/categories`, { name }, { headers });
    setCreating({ open: false, type: null, categoryId: null });
    load();
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Delete this category? All its areas must be removed first.')) return;
    try { await axios.delete(`${API}/cx/categories/${id}`, { headers }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const createArea = async (catId, name, formId) => {
    await axios.post(`${API}/cx/areas`, { category_id: catId, name, form_id: formId || null }, { headers });
    setCreating({ open: false, type: null, categoryId: null });
    load();
  };

  const bulkCreate = async (catId, prefix, start, end, formId) => {
    const r = await axios.post(`${API}/cx/areas/bulk`,
      { category_id: catId, prefix, start: +start, end: +end, form_id: formId || null }, { headers });
    toast.success(`Created ${r.data.created} areas`);
    setBulkOpen(false); load();
  };

  const regenerateQr = async (areaId) => {
    if (!window.confirm('Regenerate QR? The old QR will stop working.')) return;
    await axios.post(`${API}/cx/areas/${areaId}/regenerate-qr`, {}, { headers });
    toast.success('QR regenerated'); load();
  };

  const deleteArea = async (areaId) => {
    if (!window.confirm('Delete this area? All submissions remain in history.')) return;
    await axios.delete(`${API}/cx/areas/${areaId}`, { headers }); load();
  };

  const downloadQr = (areaId, name) => {
    const url = `${API}/cx/areas/${areaId}/qr.png?size=12`;
    axios.get(url, { headers, responseType: 'blob' }).then((r) => {
      const u = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = u; a.download = `qr-${name.replace(/\s+/g, '-')}.png`; a.click();
      URL.revokeObjectURL(u);
    });
  };

  // iter170 — canonical public URL builder (matches backend _feedback_url).
  // Prefer the DB-cached `feedback_url` if valid; otherwise rebuild from the
  // resolved base URL the API hands us. Defensive against historical bad rows.
  const copyPublicUrl = async (area) => {
    let base = baseUrlCache;
    if (!base) {
      try {
        const r = await axios.get(`${API}/cx/settings`, { headers });
        base = r.data.cx_base_url;
        setBaseUrlCache(base);
      } catch { base = ''; }
    }
    const url = isValidFeedbackUrl(area.feedback_url)
      ? area.feedback_url
      : buildFeedbackPublicUrl(base, area.qr_token);
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Public URL copied · ${area.name}`);
    } catch {
      toast.error('Copy failed. URL: ' + url);
    }
  };

  const setAreaForm = async (area, formId) => {
    await axios.put(`${API}/cx/areas/${area.id}`,
      { category_id: area.category_id, name: area.name, form_id: formId || null }, { headers });
    load();
  };

  return (
    <div className="space-y-5" data-testid="cx-structure">
      {/* Templates */}
      <Card className="border-[#E2E8DF] bg-gradient-to-r from-[var(--color-primary)] to-[#0B2A22] text-white">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#A8C5B5] mb-1">Quick start</div>
              <h3 className="text-[15px] font-semibold">Apply a seed template</h3>
              <p className="text-[12px] text-[#A8C5B5] mt-1">Only seeds categories/areas. Everything stays editable.</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {templates.map((t) => (
                <Button key={t.id} variant="outline" size="sm"
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                  onClick={() => applyTemplate(t.id)}
                  data-testid={`cx-apply-template-${t.id}`}>
                  <Sparkles className="w-3 h-3 mr-1" /> {t.label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add category button */}
      <div className="flex justify-end gap-2">
        <Link to="/admin/cx/print-qr">
          <Button size="sm" variant="outline" data-testid="cx-print-qr-btn">
            <Printer className="w-3.5 h-3.5 mr-1" /> Print QR Sheet
          </Button>
        </Link>
        <Button size="sm" onClick={() => setCreating({ open: true, type: 'category', categoryId: null })}
                data-testid="cx-add-category-btn"
                className="bg-[color:var(--color-primary)] hover:bg-[#0B2A22] text-white">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Category
        </Button>
      </div>

      {/* Categories list */}
      {cats.length === 0 && (
        <Card className="border-[#E2E8DF] border-dashed">
          <CardContent className="p-8 text-center text-[#5C6B64] text-[13px]">
            No categories yet. Apply a template above or create one manually.
          </CardContent>
        </Card>
      )}
      {cats.map((c) => (
        <Card key={c.id} className="border-[#E2E8DF]" data-testid={`cx-category-${c.id}`}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[15px] text-[#1A2421]">{c.name}</CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline"
                  onClick={() => setCreating({ open: true, type: 'area', categoryId: c.id })}
                  data-testid={`cx-add-area-${c.id}`}>
                  <Plus className="w-3 h-3 mr-1" /> Add Area
                </Button>
                <Button size="sm" variant="outline"
                  onClick={() => { setBulkCat(c.id); setBulkOpen(true); }}
                  data-testid={`cx-bulk-${c.id}`}>
                  Bulk Create
                </Button>
                <Button size="sm" variant="outline" onClick={() => deleteCategory(c.id)}
                        className="text-[#C45A41] hover:bg-red-50">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.08em] text-[#5C6B64] bg-[#F9F9F8] border-y border-[#E2E8DF]">
                  <th className="text-left px-3 py-2 font-medium">Area</th>
                  <th className="text-left px-3 py-2 font-medium">Assigned Form</th>
                  <th className="text-left px-3 py-2 font-medium">QR Token</th>
                  <th className="text-right px-3 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(areasByCat[c.id] || []).map((a) => (
                  <tr key={a.id} className="border-b border-[#F0F2EE]" data-testid={`cx-area-row-${a.id}`}>
                    <td className="px-3 py-2 text-[#1A2421]">{a.name}</td>
                    <td className="px-3 py-2">
                      <Select value={a.form_id || ''} onValueChange={(v) => setAreaForm(a, v === '_none' ? null : v)}>
                        <SelectTrigger className="h-8 text-[12px] w-56"><SelectValue placeholder="(default form)" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">(default form)</SelectItem>
                          {forms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-[#5C6B64]">{a.qr_token}</td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline" className="h-7 mr-1"
                        onClick={() => copyPublicUrl(a)}
                        data-testid={`cx-area-copy-url-${a.id}`} title="Copy public URL">
                        <Copy className="w-3 h-3 mr-1" /> URL
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 mr-1" onClick={() => downloadQr(a.id, a.name)}>
                        <Download className="w-3 h-3 mr-1" /> QR
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 mr-1" onClick={() => regenerateQr(a.id)}>
                        <RefreshCw className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-[#C45A41]" onClick={() => deleteArea(a.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {(areasByCat[c.id] || []).length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-[12px] text-[#5C6B64]">
                    No areas in this category yet.
                  </td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ))}

      {/* Create dialog */}
      <Dialog open={creating.open} onOpenChange={(o) => !o && setCreating({ open: false, type: null, categoryId: null })}>
        <DialogContent>
          <DialogHeader><DialogTitle>{creating.type === 'category' ? 'New category' : 'New area'}</DialogTitle></DialogHeader>
          <CreateForm
            type={creating.type}
            forms={forms}
            onSubmit={(payload) => creating.type === 'category'
              ? createCategory(payload.name)
              : createArea(creating.categoryId, payload.name, payload.form_id)}
          />
        </DialogContent>
      </Dialog>

      {/* Bulk dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Bulk-create areas</DialogTitle></DialogHeader>
          <BulkCreateForm forms={forms} onSubmit={(p) => bulkCreate(bulkCat, p.prefix, p.start, p.end, p.form_id)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateForm({ type, forms, onSubmit }) {
  const [name, setName] = useState(''); const [formId, setFormId] = useState('');
  return (
    <div className="space-y-3">
      <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="cx-create-name-input" /></div>
      {type === 'area' && (
        <div>
          <Label>Assign form (optional)</Label>
          <Select value={formId} onValueChange={setFormId}>
            <SelectTrigger><SelectValue placeholder="Default form" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">(default form)</SelectItem>
              {forms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <DialogFooter>
        <Button onClick={() => onSubmit({ name, form_id: formId === '_none' ? null : formId })}
                disabled={!name} data-testid="cx-create-submit-btn"
                className="bg-[color:var(--color-primary)] hover:bg-[#0B2A22] text-white">Create</Button>
      </DialogFooter>
    </div>
  );
}

function BulkCreateForm({ forms, onSubmit }) {
  const [prefix, setPrefix] = useState('Room '); const [start, setStart] = useState(101); const [end, setEnd] = useState(120);
  const [formId, setFormId] = useState('');
  return (
    <div className="space-y-3">
      <div><Label>Prefix</Label><Input value={prefix} onChange={(e) => setPrefix(e.target.value)} /></div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label>Start</Label><Input type="number" value={start} onChange={(e) => setStart(e.target.value)} /></div>
        <div><Label>End</Label><Input type="number" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
      </div>
      <div>
        <Label>Assign form (optional)</Label>
        <Select value={formId} onValueChange={setFormId}>
          <SelectTrigger><SelectValue placeholder="Default form" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_none">(default form)</SelectItem>
            {forms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="text-[11px] text-[#5C6B64]">Will create {Math.max(0, +end - +start + 1)} areas (e.g. {prefix}{start}, {prefix}{(+start) + 1} …)</div>
      <DialogFooter>
        <Button onClick={() => onSubmit({ prefix, start, end, form_id: formId === '_none' ? null : formId })}
                className="bg-[color:var(--color-primary)] hover:bg-[#0B2A22] text-white">Create</Button>
      </DialogFooter>
    </div>
  );
}

/* ────────────────── FORM BUILDER ────────────────── */
function FormsTab({ headers }) {
  const [forms, setForms] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/cx/forms`, { headers });
    setForms(r.data);
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({ name: '', description: '', questions: [] });

  const save = async (f) => {
    try {
      if (f.id) await axios.put(`${API}/cx/forms/${f.id}`, f, { headers });
      else await axios.post(`${API}/cx/forms`, f, { headers });
      toast.success('Form saved'); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this form?')) return;
    await axios.delete(`${API}/cx/forms/${id}`, { headers }); load();
  };

  return (
    <div className="space-y-4" data-testid="cx-forms">
      <div className="flex justify-end">
        <Button size="sm" onClick={startNew} data-testid="cx-new-form-btn"
                className="bg-[color:var(--color-primary)] hover:bg-[#0B2A22] text-white">
          <Plus className="w-3.5 h-3.5 mr-1" /> New Form
        </Button>
      </div>
      {forms.length === 0 && (
        <Card className="border-[#E2E8DF] border-dashed"><CardContent className="p-8 text-center text-[#5C6B64] text-[13px]">
          No forms yet. Create one to assign to areas.
        </CardContent></Card>
      )}
      {forms.map((f) => (
        <Card key={f.id} className="border-[#E2E8DF]" data-testid={`cx-form-${f.id}`}>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <div className="font-medium text-[#1A2421]">{f.name}</div>
              <div className="text-[11.5px] text-[#5C6B64]">{f.questions.length} question{f.questions.length === 1 ? '' : 's'}</div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setEditing(f)}>Edit</Button>
              <Button size="sm" variant="outline" onClick={() => remove(f.id)} className="text-[#C45A41]">
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}

      {editing && <FormBuilderDialog initial={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function FormBuilderDialog({ initial, onClose, onSave }) {
  const { getHeaders } = useAuth();
  const headers = useMemo(() => getHeaders(), [getHeaders]);
  const [form, setForm] = useState({
    id: initial.id, name: initial.name || '', description: initial.description || '',
    questions: initial.questions || [],
  });
  const [tab, setTab] = useState('questions');

  const addQ = (type) => setForm((p) => ({
    ...p, questions: [...p.questions, {
      type, label: `New ${type.replace('_', ' ')} question`, required: false,
      options: (type === 'multiple_choice' || type === 'multi_select')
        ? ['Option 1', 'Option 2'] : undefined,
    }],
  }));
  const updateQ = (i, patch) => setForm((p) => ({
    ...p, questions: p.questions.map((q, idx) => idx === i ? { ...q, ...patch } : q),
  }));
  const removeQ = (i) => setForm((p) => ({ ...p, questions: p.questions.filter((_, idx) => idx !== i) }));
  const duplicateQ = (i) => setForm((p) => {
    const src = p.questions[i];
    if (!src) return p;
    const { id: _drop, ...rest } = src;
    const clone = { ...rest, options: src.options ? [...src.options] : undefined };
    const arr = [...p.questions];
    arr.splice(i + 1, 0, clone);
    return { ...p, questions: arr };
  });
  const moveQ = (i, dir) => setForm((p) => {
    const arr = [...p.questions]; const j = i + dir;
    if (j < 0 || j >= arr.length) return p;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...p, questions: arr };
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="cx-form-builder-dialog">
        <DialogHeader>
          <DialogTitle>{initial.id ? 'Edit form' : 'New form'}</DialogTitle>
          <div className="text-[11.5px] text-[#5C6B64]">
            Build your feedback form, customize the guest-facing brand, and preview live — all in one place.
          </div>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList className="bg-[#F4F7F4] border border-[#E2E8DF]">
            <TabsTrigger value="questions"  data-testid="cx-fb-tab-questions">Questions</TabsTrigger>
            <TabsTrigger value="theme"      data-testid="cx-fb-tab-theme">Theme &amp; Branding</TabsTrigger>
            <TabsTrigger value="preview"    data-testid="cx-fb-tab-preview">Preview</TabsTrigger>
          </TabsList>

          <TabsContent value="questions" className="mt-4">
            <div className="space-y-3">
              <div>
                <Label>Form name</Label>
                <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                       data-testid="cx-form-name" />
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Textarea rows={2} value={form.description}
                          onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
              </div>

              <div className="border-t border-[#E2E8DF] pt-3">
                <div className="flex items-center justify-between mb-2">
                  <Label>Questions</Label>
                  <Select onValueChange={(v) => addQ(v)}>
                    <SelectTrigger className="h-8 w-[200px] text-[12px]" data-testid="cx-add-question-select">
                      <SelectValue placeholder="+ Add question" />
                    </SelectTrigger>
                    <SelectContent>
                      {QUESTION_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  {form.questions.length === 0 && (
                    <div className="text-[12px] text-[#5C6B64] text-center py-6 border border-dashed border-[#E2E8DF] rounded-md">
                      Add at least one question.
                    </div>
                  )}
                  {form.questions.map((q, i) => (
                    <Card key={i} className="border-[#E2E8DF]" data-testid={`cx-q-${i}`}>
                      <CardContent className="p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="text-[10px] uppercase tracking-wider text-[#5C6B64] bg-[#F0F2EE] px-2 py-0.5 rounded">
                            {i + 1}. {q.type.replace('_', ' ')}
                          </div>
                          <div className="ml-auto flex gap-1">
                            <Button size="sm" variant="outline" className="h-6 w-6 p-0"
                              onClick={() => moveQ(i, -1)} title="Move up"
                              data-testid={`cx-q-${i}-up`}>↑</Button>
                            <Button size="sm" variant="outline" className="h-6 w-6 p-0"
                              onClick={() => moveQ(i, +1)} title="Move down"
                              data-testid={`cx-q-${i}-down`}>↓</Button>
                            <Button size="sm" variant="outline" className="h-6 w-6 p-0"
                              onClick={() => duplicateQ(i)} title="Duplicate question"
                              data-testid={`cx-q-${i}-duplicate`}>
                              <Plus className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="h-6 w-6 p-0 text-[#C45A41]"
                              onClick={() => removeQ(i)} title="Delete question"
                              data-testid={`cx-q-${i}-delete`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <Input value={q.label} placeholder="Question label"
                          data-testid={`cx-q-${i}-label`}
                          onChange={(e) => updateQ(i, { label: e.target.value })} />
                        {(q.type === 'multiple_choice' || q.type === 'multi_select') && (
                          <div>
                            <Textarea rows={3}
                              value={(q.options || []).join('\n')}
                              placeholder="One option per line"
                              data-testid={`cx-q-${i}-options`}
                              onChange={(e) => updateQ(i, { options: e.target.value.split('\n').filter(Boolean) })} />
                            <div className="text-[10px] text-[#5C6B64] mt-1">
                              {q.type === 'multi_select'
                                ? 'Guest can select multiple options (checkboxes).'
                                : 'Guest picks exactly one (radio buttons).'}
                            </div>
                          </div>
                        )}
                        <label className="text-[11.5px] text-[#5C6B64] flex items-center gap-1">
                          <input type="checkbox" checked={!!q.required}
                            data-testid={`cx-q-${i}-required`}
                            onChange={(e) => updateQ(i, { required: e.target.checked })} />
                          Required
                        </label>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="theme" className="mt-4">
            <ThemeBrandingPanel headers={headers} />
          </TabsContent>

          <TabsContent value="preview" className="mt-4">
            <PreviewPanel headers={headers} />
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(form)} disabled={!form.name || form.questions.length === 0}
                  className="bg-[color:var(--color-primary)] hover:bg-[#0B2A22] text-white" data-testid="cx-save-form-btn">
            Save form
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ────────────────── THEME & BRANDING PANEL (inside form builder) ────────────────── */
function ThemeBrandingPanel({ headers }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cx/settings/landing`, { headers });
      setData(r.data);
    } catch { setData({}); }
  }, [headers]);

  useEffect(() => { reload(); }, [reload]);

  if (!data) return <div className="text-[12.5px] text-[#5C6B64] py-6 text-center">Loading theme settings…</div>;

  const set = (k, v) => setData((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...data };
      // Send falsy strings as empty so server can clear them.
      const r = await axios.put(`${API}/cx/settings/landing`, payload, { headers });
      setData(r.data);
      toast.success('Theme & branding saved · applies to all guest forms');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const CARD_STYLES = [
    { v: 'elevated', label: 'Elevated', hint: 'Soft shadow, premium' },
    { v: 'flat',     label: 'Flat',     hint: 'Minimal, no shadow' },
    { v: 'bordered', label: 'Bordered', hint: 'Crisp outline' },
    { v: 'glass',    label: 'Glass',    hint: 'Frosted backdrop' },
  ];
  const RADII = [
    { v: 'none', label: 'Square' }, { v: 'sm', label: 'XS' },
    { v: 'md', label: 'S' }, { v: 'lg', label: 'M' }, { v: 'xl', label: 'L' },
  ];
  const THEMES = [
    { v: 'light', label: 'Light', hint: 'Bright luxury' },
    { v: 'dark',  label: 'Dark',  hint: 'Boutique evening' },
    { v: 'auto',  label: 'Auto',  hint: 'Follow device' },
  ];
  const FONTS = [
    { v: 'sans',    label: 'Sans',    sample: 'Aa' },
    { v: 'serif',   label: 'Serif',   sample: 'Aa' },
    { v: 'display', label: 'Display', sample: 'Aa' },
  ];

  return (
    <div className="space-y-5" data-testid="cx-theme-panel">
      <div className="bg-[#F1F7F2] border border-[#52A57B]/30 rounded-md p-3 text-[11.5px] text-[#2D6A4F] flex items-start gap-2">
        <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          Theme &amp; branding apply to <b>every guest feedback form</b> in this tenant.
          The logo is auto-inherited from your business branding — no duplicate uploads needed.
        </span>
      </div>

      {/* ── Identity ──────────────────────────────────────────────── */}
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-[0.16em] font-medium text-[#5C6B64]">Identity</div>
        <div>
          <Label className="text-[11px]">Brand name on guest form</Label>
          <Input value={data.cx_brand_name || ''} onChange={(e) => set('cx_brand_name', e.target.value)}
                 placeholder="Auto-inherits from outlet / tenant brand"
                 data-testid="cx-theme-brand-name" />
          <div className="text-[10px] text-[#5C6B64] mt-1">Leave blank to auto-inherit from your business branding.</div>
        </div>
        <label className="flex items-center gap-2 text-[12px] text-[#1A2421]">
          <input type="checkbox" checked={!!data.cx_show_brand_name}
                 onChange={(e) => set('cx_show_brand_name', e.target.checked)}
                 data-testid="cx-theme-show-brand-name" />
          Show brand name in hero header
        </label>
        <label className="flex items-center gap-2 text-[12px] text-[#1A2421]">
          <input type="checkbox" checked={!!data.cx_show_platform_branding}
                 onChange={(e) => set('cx_show_platform_branding', e.target.checked)}
                 data-testid="cx-theme-show-platform" />
          Show &quot;Powered by RestroPulse&quot; footer (off by default)
        </label>
      </div>

      {/* ── Logo upload (CX override) ─────────────────────────────── */}
      <ImageUploadField
        label="CX Logo"
        hint="Square or transparent PNG works best · max 3 MB · JPG/PNG/WEBP. Leave empty to auto-inherit from your outlet/tenant logo."
        endpoint={`${API}/cx/branding/logo`}
        previewUrl={data.has_cx_logo ? `${API}/cx/branding/${data.tenant_id || 'me'}/logo?v=${data.updated_at || ''}` : null}
        present={!!data.has_cx_logo}
        headers={headers}
        onChange={() => reload()}
        testId="cx-theme-logo-upload"
        recommendedAspect="1:1"
      />

      {/* ── Hero banner upload ────────────────────────────────────── */}
      <ImageUploadField
        label="Hero Banner"
        hint="Recommended 1600×640 (5:2) · max 3 MB · keep important elements centred for mobile."
        endpoint={`${API}/cx/branding/hero`}
        previewUrl={data.has_cx_hero ? `${API}/cx/branding/${data.tenant_id || 'me'}/hero?v=${data.updated_at || ''}` : null}
        present={!!data.has_cx_hero}
        headers={headers}
        onChange={() => reload()}
        testId="cx-theme-hero-upload"
        recommendedAspect="5:2"
        tall
      />

      {/* ── Colors ─────────────────────────────────────────────────── */}
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-[0.16em] font-medium text-[#5C6B64]">Colors</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <ColorField label="Primary color" value={data.cx_primary_color || 'var(--color-primary)'}
                      onChange={(v) => set('cx_primary_color', v)} testId="cx-theme-primary-color" />
          <ColorField label="Background color" value={data.cx_background_color || '#FAFBF8'}
                      onChange={(v) => set('cx_background_color', v)} testId="cx-theme-bg-color" />
        </div>
      </div>

      {/* ── Card style ────────────────────────────────────────────── */}
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-[0.16em] font-medium text-[#5C6B64]">Card style</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {CARD_STYLES.map((c) => (
            <button key={c.v} type="button"
              onClick={() => set('cx_card_style', c.v)}
              className={`text-left rounded-md border p-2.5 transition ${
                data.cx_card_style === c.v
                  ? 'border-[color:var(--color-primary)] bg-[#EEF4EF]'
                  : 'border-[#E2E8DF] bg-white hover:border-[color:var(--color-primary)]/40'}`}
              data-testid={`cx-theme-card-${c.v}`}>
              <div className="text-[12px] font-medium text-[#1A2421]">{c.label}</div>
              <div className="text-[10.5px] text-[#5C6B64]">{c.hint}</div>
            </button>
          ))}
        </div>
      </div>

      {/* ── Border radius ─────────────────────────────────────────── */}
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-[0.16em] font-medium text-[#5C6B64]">Corner radius</div>
        <div className="flex gap-2 flex-wrap">
          {RADII.map((r) => (
            <button key={r.v} type="button" onClick={() => set('cx_border_radius', r.v)}
              className={`text-[11.5px] px-3 py-1.5 rounded-md border transition ${
                data.cx_border_radius === r.v
                  ? 'border-[color:var(--color-primary)] bg-[#EEF4EF] font-medium'
                  : 'border-[#E2E8DF] bg-white hover:border-[color:var(--color-primary)]/40'}`}
              data-testid={`cx-theme-radius-${r.v}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Theme mode ────────────────────────────────────────────── */}
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-[0.16em] font-medium text-[#5C6B64]">Theme mode</div>
        <div className="grid grid-cols-3 gap-2">
          {THEMES.map((t) => (
            <button key={t.v} type="button" onClick={() => set('cx_theme_mode', t.v)}
              className={`text-left rounded-md border p-2.5 transition ${
                data.cx_theme_mode === t.v
                  ? 'border-[color:var(--color-primary)] bg-[#EEF4EF]'
                  : 'border-[#E2E8DF] bg-white hover:border-[color:var(--color-primary)]/40'}`}
              data-testid={`cx-theme-mode-${t.v}`}>
              <div className="text-[12px] font-medium text-[#1A2421]">{t.label}</div>
              <div className="text-[10.5px] text-[#5C6B64]">{t.hint}</div>
            </button>
          ))}
        </div>
      </div>

      {/* ── Typography ────────────────────────────────────────────── */}
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-[0.16em] font-medium text-[#5C6B64]">Typography</div>
        <div className="grid grid-cols-3 gap-2">
          {FONTS.map((f) => (
            <button key={f.v} type="button" onClick={() => set('cx_font_family', f.v)}
              className={`rounded-md border p-3 transition flex items-center justify-between ${
                data.cx_font_family === f.v
                  ? 'border-[color:var(--color-primary)] bg-[#EEF4EF]'
                  : 'border-[#E2E8DF] bg-white hover:border-[color:var(--color-primary)]/40'}`}
              data-testid={`cx-theme-font-${f.v}`}>
              <span className="text-[12px] font-medium text-[#1A2421]">{f.label}</span>
              <span className={`text-[18px] text-[color:var(--color-primary)] ${
                f.v === 'serif' ? 'font-serif' : f.v === 'display' ? 'font-mono italic' : 'font-sans'
              }`}>{f.sample}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Welcome + Thank-you copy ──────────────────────────────── */}
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-[0.16em] font-medium text-[#5C6B64]">Welcome &amp; Thank-you copy</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px]">Welcome title</Label>
            <Input value={data.cx_welcome_title || ''}
              onChange={(e) => set('cx_welcome_title', e.target.value)}
              data-testid="cx-theme-welcome-title" />
          </div>
          <div>
            <Label className="text-[11px]">Welcome subtitle</Label>
            <Input value={data.cx_welcome_subtitle || ''}
              onChange={(e) => set('cx_welcome_subtitle', e.target.value)}
              data-testid="cx-theme-welcome-sub" />
          </div>
          <div>
            <Label className="text-[11px]">Thank-you title</Label>
            <Input value={data.cx_thank_you_title || ''}
              onChange={(e) => set('cx_thank_you_title', e.target.value)}
              data-testid="cx-theme-ty-title" />
          </div>
          <div>
            <Label className="text-[11px]">Thank-you message</Label>
            <Textarea rows={2} value={data.cx_thank_you_message || ''}
              onChange={(e) => set('cx_thank_you_message', e.target.value)}
              data-testid="cx-theme-ty-msg" />
          </div>
        </div>
      </div>

      {/* ── Guest identity requirements (iter176) ─────────────────── */}
      <div className="space-y-2 rounded-md border border-[#D89B5B]/30 bg-[#FFFBF4] p-3">
        <div className="text-[10px] uppercase tracking-[0.16em] font-medium text-[#9C6815] flex items-center gap-1.5">
          <AlertTriangle className="w-3 h-3" /> Guest identity (highly recommended)
        </div>
        <div className="text-[11px] text-[#5C6B64]">
          Phone number is the primary identity key for repeat-guest mapping,
          sentiment history, complaint follow-up, and future WhatsApp engagement.
          Disabling these reduces operational traceability.
        </div>
        <label className="flex items-center gap-2 text-[12px] text-[#1A2421] pt-1">
          <input type="checkbox" checked={data.cx_require_guest_name !== false}
                 onChange={(e) => set('cx_require_guest_name', e.target.checked)}
                 data-testid="cx-theme-require-name" />
          Require guest name
        </label>
        <label className="flex items-center gap-2 text-[12px] text-[#1A2421]">
          <input type="checkbox" checked={data.cx_require_guest_phone !== false}
                 onChange={(e) => set('cx_require_guest_phone', e.target.checked)}
                 data-testid="cx-theme-require-phone" />
          Require mobile number (recommended for guest traceability)
        </label>
      </div>

      <div className="flex justify-end pt-2">
        <Button onClick={save} disabled={saving}
                className="bg-[color:var(--color-primary)] hover:bg-[#0B2A22] text-white"
                data-testid="cx-theme-save">
          {saving ? 'Saving…' : 'Save theme & branding'}
        </Button>
      </div>
    </div>
  );
}

function ColorField({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-[11px]">{label}</Label>
      <div className="flex items-center gap-2">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)}
               className="h-10 w-10 border border-[#E2E8DF] rounded cursor-pointer"
               data-testid={`${testId}-picker`} />
        <Input value={value} onChange={(e) => onChange(e.target.value)}
               className="font-mono text-[12px]" data-testid={testId} />
      </div>
    </div>
  );
}

/* ─── Drag-drop image upload with preview + remove (iter176) ──────────── */
function ImageUploadField({
  label, hint, endpoint, previewUrl, present, headers,
  onChange, testId, recommendedAspect = '1:1', tall = false,
}) {
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.error('Only PNG, JPG, or WEBP images are supported');
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Image must be under 3 MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(endpoint, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${label} uploaded`);
      await onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Upload failed`);
    } finally { setUploading(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${label.toLowerCase()}?`)) return;
    try {
      await axios.delete(endpoint, { headers });
      toast.success(`${label} removed`);
      await onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove failed');
    }
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.16em] font-medium text-[#5C6B64]">{label}</div>
        <span className="text-[10px] text-[#5C6B64] bg-[#F0F2EE] px-2 py-0.5 rounded-full">
          {recommendedAspect}
        </span>
      </div>
      {present && previewUrl ? (
        <div className="space-y-2">
          <div className={`relative border border-[#E2E8DF] rounded-md overflow-hidden bg-[#F4F7F4] ${
            tall ? '' : 'h-32 flex items-center justify-center'
          }`}
               style={tall ? { aspectRatio: '5/2' } : undefined}>
            <img src={previewUrl} alt={`${label} preview`}
                 className={tall ? 'w-full h-full object-cover' : 'max-h-28 max-w-[80%] object-contain'} />
          </div>
          <div className="flex gap-2">
            <label className="flex-1 text-center text-[12px] font-medium px-3 py-2 rounded-md border border-[color:var(--color-primary)] text-[color:var(--color-primary)] hover:bg-[#EEF4EF] cursor-pointer transition"
                   data-testid={`${testId}-replace`}>
              {uploading ? 'Uploading…' : 'Replace'}
              <input type="file" hidden accept="image/png,image/jpeg,image/jpg,image/webp"
                     onChange={(e) => upload(e.target.files?.[0])} />
            </label>
            <Button variant="outline" size="sm" onClick={remove}
                    className="text-[#C45A41] border-[#C45A41]/30 hover:bg-[#FCEDE7]"
                    data-testid={`${testId}-remove`}>
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <label
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault(); setDragging(false);
            upload(e.dataTransfer.files?.[0]);
          }}
          className={`flex flex-col items-center justify-center text-center cursor-pointer rounded-md border-2 border-dashed transition ${
            dragging ? 'border-[color:var(--color-primary)] bg-[#EEF4EF]' : 'border-[#E2E8DF] bg-[#FAFBF8] hover:border-[color:var(--color-primary)]/60'
          } ${tall ? 'p-8' : 'p-6'}`}
          data-testid={`${testId}-dropzone`}
        >
          <Download className="w-6 h-6 text-[#9AAA9F] mb-1.5 rotate-180" />
          <div className="text-[12px] font-medium text-[#1A2421]">
            {uploading ? 'Uploading…' : `Drop image or click to upload`}
          </div>
          <div className="text-[10.5px] text-[#5C6B64] mt-1">{hint}</div>
          <input type="file" hidden accept="image/png,image/jpeg,image/jpg,image/webp"
                 onChange={(e) => upload(e.target.files?.[0])} />
        </label>
      )}
    </div>
  );
}

/* ────────────────── PREVIEW PANEL ────────────────── */
function PreviewPanel({ headers }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [device, setDevice] = useState('mobile');

  useEffect(() => {
    // Use the first area's QR token as a representative preview target.
    axios.get(`${API}/cx/areas`, { headers })
      .then((r) => {
        const areas = r.data || [];
        if (!areas.length) { setPreviewUrl(null); return; }
        // Build canonical URL from the same builder the rest of the app uses.
        axios.get(`${API}/cx/settings`, { headers })
          .then((s) => {
            const base = s.data?.cx_base_url || window.location.origin;
            const url = buildFeedbackPublicUrl(base, areas[0].qr_token);
            if (isValidFeedbackUrl(url)) setPreviewUrl(url);
          });
      })
      .catch(() => setPreviewUrl(null));
  }, [headers]);

  if (previewUrl === null) {
    return (
      <div className="text-center py-10 text-[#5C6B64] text-[12.5px]" data-testid="cx-preview-empty">
        Add at least one area in Categories &amp; Areas to enable live preview.
      </div>
    );
  }

  const widths = { mobile: 390, tablet: 768, desktop: 1100 };
  return (
    <div className="space-y-3" data-testid="cx-preview-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[12px] text-[#5C6B64]">
          Live preview · scans the public form at{' '}
          <a href={previewUrl} target="_blank" rel="noreferrer"
             className="text-[color:var(--color-primary)] underline" data-testid="cx-preview-open">{previewUrl}</a>
        </div>
        <div className="inline-flex border border-[#E2E8DF] rounded-md overflow-hidden">
          {['mobile', 'tablet', 'desktop'].map((d) => (
            <button key={d} type="button" onClick={() => setDevice(d)}
                    className={`text-[11.5px] px-3 py-1.5 capitalize ${
                      device === d ? 'bg-[color:var(--color-primary)] text-white' : 'bg-white text-[#1A2421] hover:bg-[#F4F7F4]'
                    }`}
                    data-testid={`cx-preview-${d}`}>
              {d}
            </button>
          ))}
        </div>
      </div>
      <div className="bg-[#F0F2EE] rounded-md p-4 flex justify-center">
        <iframe key={device} title="Public form preview" src={previewUrl}
                style={{ width: widths[device], height: 620, maxWidth: '100%' }}
                className="border border-[#E2E8DF] rounded-md bg-white shadow-sm"
                data-testid="cx-preview-iframe" />
      </div>
    </div>
  );
}

/* ────────────────── SUBMISSIONS ────────────────── */
function SubmissionsTab({ headers }) {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [areas, setAreas] = useState([]);
  const [filters, setFilters] = useState({
    sentiment: 'all',
    area_id: 'all',
    category_id: 'all',
    min_rating: 'all',
  });
  const [dateRange, setDateRange] = useState(() => {
    const saved = loadSavedRange();
    return { ...saved, resolved: resolveRange(saved.preset, saved.custom) };
  });
  const [drill, setDrill] = useState(null);

  // Load filter options once.
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/cx/categories`, { headers }),
      axios.get(`${API}/cx/areas`, { headers }),
    ]).then(([c, a]) => { setCats(c.data || []); setAreas(a.data || []); });
  }, [headers]);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    params.set('start_date', dateRange.resolved.start_date);
    params.set('end_date', dateRange.resolved.end_date);
    params.set('days', String(dateRange.resolved.days));
    if (filters.sentiment !== 'all') params.set('sentiment', filters.sentiment);
    if (filters.area_id !== 'all') params.set('area_id', filters.area_id);
    if (filters.category_id !== 'all') params.set('category_id', filters.category_id);
    if (filters.min_rating !== 'all') params.set('min_rating', filters.min_rating);
    const r = await axios.get(`${API}/cx/submissions?${params}`, { headers });
    setItems(r.data);
  }, [headers, filters, dateRange]);

  useEffect(() => { load(); }, [load]);

  // When category filter is set, narrow the area dropdown to matching areas.
  const visibleAreas = filters.category_id === 'all'
    ? areas
    : areas.filter((a) => a.category_id === filters.category_id);

  const updateFilter = (k, v) => setFilters((p) => ({
    ...p, [k]: v,
    // Reset area filter when category changes (to avoid inconsistency).
    ...(k === 'category_id' ? { area_id: 'all' } : {}),
  }));

  const clear = () => setFilters({ sentiment: 'all', area_id: 'all', category_id: 'all', min_rating: 'all' });
  const hasActiveFilter = Object.entries(filters)
    .some(([, v]) => v !== 'all' && v !== '');

  return (
    <div className="space-y-4" data-testid="cx-submissions">
      {/* ─── Header strip with date range ─── */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[12px] text-[#5C6B64]">
          {dateRange.resolved.start_date} → {dateRange.resolved.end_date} · {items.length} submission{items.length === 1 ? '' : 's'}
        </div>
        <CxDateRangeFilter value={dateRange} onChange={setDateRange} testId="cx-submissions-daterange" />
      </div>
      {/* ─── Filter bar ─── */}
      <Card className="border-[#E2E8DF]">
        <CardContent className="p-3 flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.12em] text-[#5C6B64]">Category</Label>
            <Select value={filters.category_id} onValueChange={(v) => updateFilter('category_id', v)}>
              <SelectTrigger className="h-8 w-[160px] text-[12px]" data-testid="cx-filter-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.12em] text-[#5C6B64]">Area</Label>
            <Select value={filters.area_id} onValueChange={(v) => updateFilter('area_id', v)}>
              <SelectTrigger className="h-8 w-[160px] text-[12px]" data-testid="cx-filter-area"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All areas</SelectItem>
                {visibleAreas.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.12em] text-[#5C6B64]">Sentiment</Label>
            <Select value={filters.sentiment} onValueChange={(v) => updateFilter('sentiment', v)}>
              <SelectTrigger className="h-8 w-[140px] text-[12px]" data-testid="cx-filter-sentiment"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="positive">Positive</SelectItem>
                <SelectItem value="neutral">Neutral</SelectItem>
                <SelectItem value="negative">Negative</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.12em] text-[#5C6B64]">Min Rating</Label>
            <Select value={filters.min_rating} onValueChange={(v) => updateFilter('min_rating', v)}>
              <SelectTrigger className="h-8 w-[120px] text-[12px]" data-testid="cx-filter-rating"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any rating</SelectItem>
                <SelectItem value="4">★ 4+ only</SelectItem>
                <SelectItem value="3">★ 3+ only</SelectItem>
                <SelectItem value="2">★ 2+ only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {hasActiveFilter && (
            <Button variant="outline" size="sm" className="h-8" onClick={clear} data-testid="cx-filter-clear">
              Clear filters
            </Button>
          )}
          <div className="ml-auto text-[11.5px] text-[#5C6B64]">
            {items.length} submission{items.length === 1 ? '' : 's'}
          </div>
        </CardContent>
      </Card>

      <Card className="border-[#E2E8DF]">
        <CardContent className="p-0">
          <table className="w-full text-[12.5px]">
            <thead className="bg-[#F9F9F8] border-b border-[#E2E8DF]">
              <tr className="text-[10px] uppercase tracking-[0.08em] text-[#5C6B64]">
                <th className="text-left px-3 py-2 font-medium">When</th>
                <th className="text-left px-3 py-2 font-medium">Guest</th>
                <th className="text-left px-3 py-2 font-medium">Area</th>
                <th className="text-right px-3 py-2 font-medium">NPS</th>
                <th className="text-right px-3 py-2 font-medium">Rating</th>
                <th className="text-left px-3 py-2 font-medium">Sentiment</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-b border-[#F0F2EE] hover:bg-[#F9F9F8] cursor-pointer"
                    onClick={() => s.area_id && setDrill({ area_id: s.area_id })}
                    data-testid={`cx-submission-${s.id}`}>
                  <td className="px-3 py-2 text-[#5C6B64] whitespace-nowrap">{(s.submitted_at || '').slice(0, 16).replace('T', ' ')}</td>
                  <td className="px-3 py-2 text-[#1A2421]">
                    {s.guest_name || <span className="text-[#5C6B64]">Anonymous</span>}
                    {s.guest_phone && <span className="text-[10.5px] text-[#5C6B64] ml-1">· {s.guest_phone}</span>}
                  </td>
                  <td className="px-3 py-2 text-[#1A2421]">
                    {s.category_name && (
                      <span className="text-[10px] text-[#5C6B64] uppercase tracking-wider mr-1">
                        {s.category_name} ›
                      </span>
                    )}
                    <span className="font-medium">{s.area_name || '(deleted area)'}</span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {s.nps_score != null ? (
                      <span title={s.nps_derived ? 'Derived from rating' : 'Explicit NPS'}>
                        {s.nps_score}
                        {s.nps_derived && <sup className="text-[8.5px] text-[#5C6B64] ml-0.5">†</sup>}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{s.avg_rating ?? '—'}</td>
                  <td className="px-3 py-2">
                    <SentimentChip s={s.sentiment} />
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-[#5C6B64] text-[12.5px]">No submissions match the current filters.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="text-[10.5px] text-[#5C6B64] flex items-center gap-1">
        <sup>†</sup> NPS derived from rating questions when no explicit NPS question exists.
      </div>

      {drill && (
        <AreaDrilldownDialog
          areaId={drill.area_id}
          headers={headers}
          onClose={() => setDrill(null)} />
      )}
    </div>
  );
}

function SentimentChip({ s }) {
  const cfg = {
    positive: { bg: '#EEF4EF', fg: '#2D6A4F', text: 'Positive' },
    neutral:  { bg: '#F4ECE2', fg: '#D89B5B', text: 'Neutral' },
    negative: { bg: '#FCE8E2', fg: '#C45A41', text: 'Negative' },
  }[s] || { bg: '#F0F2EE', fg: '#5C6B64', text: s || '—' };
  return <span className="text-[11px] px-2 py-0.5 rounded-full font-medium" style={{ background: cfg.bg, color: cfg.fg }}>{cfg.text}</span>;
}

/* ────────────────── SETTINGS ────────────────── */
function SettingsTab({ headers }) {
  const [baseUrl, setBaseUrl] = useState('');
  const [resolved, setResolved] = useState('');
  const [areas, setAreas] = useState([]);
  const [saving, setSaving] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  // QR health audit — populated on mount, refreshed after save/resync.
  const [health, setHealth] = useState(null);

  const loadHealth = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cx/settings/health`, { headers });
      setHealth(r.data);
    } catch { setHealth(null); }
  }, [headers]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/cx/settings`, { headers }),
      axios.get(`${API}/cx/areas`, { headers }),
    ]).then(([s, a]) => {
      setBaseUrl(s.data.cx_base_url);
      setResolved(s.data.cx_base_url);
      setAreas(a.data || []);
    });
    loadHealth();
  }, [headers, loadHealth]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/cx/settings`, { cx_base_url: baseUrl }, { headers });
      toast.success(`Saved · ${r.data.areas_updated} QR URL${r.data.areas_updated === 1 ? '' : 's'} refreshed`);
      setResolved(r.data.cx_base_url);
      await loadHealth();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const resync = async () => {
    setResyncing(true);
    try {
      const r = await axios.post(`${API}/cx/settings/resync-urls`, null, { headers });
      const msg = r.data.fixed > 0
        ? `Re-sync complete · ${r.data.fixed} URL${r.data.fixed === 1 ? '' : 's'} healed (${r.data.malformed_before} were malformed)`
        : `Re-sync complete · all ${r.data.scanned} URL${r.data.scanned === 1 ? '' : 's'} were already valid`;
      toast.success(msg);
      await loadHealth();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Re-sync failed'); }
    finally { setResyncing(false); }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('URL copied to clipboard');
  };

  // Example area for a live preview link. Picks the first area; falls back to a placeholder token.
  const previewArea = areas[0];
  const previewToken = previewArea?.qr_token || 'EXAMPLE_TOKEN';
  // Use shared helper so display can never diverge from actual generation.
  const previewUrl = buildFeedbackPublicUrl(resolved, previewToken);
  const exampleUrl = buildFeedbackPublicUrl(resolved, 'EXAMPLE_TOKEN');

  // Health pill colour scheme.
  const healthClass = health == null ? 'bg-[#F0F2EE] text-[#5C6B64]'
    : health.all_valid ? 'bg-[#EEF4EF] text-[color:var(--color-primary)] border border-[color:var(--color-primary)]/20'
    : 'bg-[#FEF3EC] text-[#C45A41] border border-[#C45A41]/30';
  const healthLabel = health == null ? 'Checking…'
    : health.all_valid ? `All ${health.total} URLs healthy`
    : `${health.invalid + health.base_mismatch} of ${health.total} need re-sync`;

  return (
    <div className="space-y-4 max-w-2xl" data-testid="cx-settings-tab">
      {/* ─── QR Health Status ─── */}
      <Card className="border-[#E2E8DF]" data-testid="cx-health-card">
        <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#5C6B64] font-medium mb-1">
              QR Health Status
            </div>
            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-medium ${healthClass}`}
                 data-testid="cx-health-pill">
              {health && health.all_valid && (
                <svg viewBox="0 0 16 16" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M3 8.5l3 3 7-7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
              {health && !health.all_valid && <AlertTriangle className="w-3 h-3" />}
              {healthLabel}
            </div>
            {health && !health.all_valid && (
              <p className="text-[11px] text-[#5C6B64] mt-2 leading-snug">
                {health.invalid > 0 && <>{health.invalid} URL{health.invalid === 1 ? '' : 's'} malformed · </>}
                {health.base_mismatch > 0 && <>{health.base_mismatch} pointing at an old base URL · </>}
                Click <span className="font-medium text-[#1A2421]">Re-sync</span> to heal them (tokens stay unchanged so existing printed QRs keep working).
              </p>
            )}
          </div>
          <Button onClick={resync} disabled={resyncing || (health && health.all_valid)}
            variant="outline" data-testid="cx-resync-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${resyncing ? 'animate-spin' : ''}`} />
            {resyncing ? 'Re-syncing…' : 'Re-sync all URLs'}
          </Button>
        </CardContent>
      </Card>

      {/* ─── Public Base URL ─── */}
      <Card className="border-[#E2E8DF]">
        <CardHeader className="pb-2"><CardTitle className="text-[15px]">Current public base URL</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-[12.5px] text-[#5C6B64]">
            Every QR code your guests scan encodes <code className="text-[11px] bg-[#F0F2EE] px-1.5 py-0.5 rounded">[base]/feedback/[token]</code>.
            Saving this updates the URL embedded in every existing QR — no need to reprint per change.
          </p>
          <div>
            <Label className="text-[12px]">Base URL</Label>
            <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)}
                   placeholder="https://your-domain.com" data-testid="cx-base-url-input" />
            <p className="text-[10.5px] text-[#5C6B64] mt-1">
              Use your white-label domain (e.g. <code>https://hotelname.com</code>) or leave the default preview host.
              Must start with <code>http://</code> or <code>https://</code> and must NOT include a path.
            </p>
          </div>
          {/* Example generated URL — guests will see this exact format. */}
          <div className="bg-[#F9F9F8] border border-[#E2E8DF] rounded-md p-2.5">
            <div className="text-[9.5px] uppercase tracking-[0.12em] text-[#5C6B64] font-medium mb-1">
              Example generated URL
            </div>
            <code className="text-[11.5px] font-mono text-[#1A2421] break-all" data-testid="cx-example-url">
              {exampleUrl}
            </code>
          </div>
          <Button onClick={save} disabled={saving} className="bg-[color:var(--color-primary)] hover:bg-[#0B2A22] text-white"
                  data-testid="cx-base-url-save-btn">
            {saving ? 'Saving…' : 'Save & refresh QRs'}
          </Button>
        </CardContent>
      </Card>

      {/* ─── Live resolved URL preview + test link ─── */}
      <Card className="border-[#E2E8DF] bg-[#F9F9F8]" data-testid="cx-resolved-url-card">
        <CardContent className="p-4 space-y-2">
          <div className="text-[10px] uppercase tracking-[0.12em] text-[#5C6B64] font-medium">
            Live resolved URL (first area)
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <code className="text-[12px] bg-white border border-[#E2E8DF] px-2 py-1 rounded font-mono text-[#1A2421] break-all flex-1"
                  data-testid="cx-resolved-url">
              {previewUrl}
            </code>
            <Button size="sm" variant="outline" className="h-8" onClick={() => copyUrl(previewUrl)}
                    data-testid="cx-copy-url-btn">
              <Copy className="w-3 h-3 mr-1" /> Copy
            </Button>
            {previewArea && (
              <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                <Button size="sm" variant="outline" className="h-8" data-testid="cx-test-url-btn">
                  Test scan ↗
                </Button>
              </a>
            )}
          </div>
          <p className="text-[10.5px] text-[#5C6B64] leading-relaxed">
            This is exactly what guests see when they scan{' '}
            <span className="font-medium text-[#1A2421]">{previewArea?.name || 'an area'}</span>'s QR code.
            Open it on your phone to verify routing — should load the public form directly with no login.
          </p>
        </CardContent>
      </Card>

      {/* ─── Landing page customization (iter171) ─── */}
      <LandingPageCard headers={headers} previewUrl={previewUrl} />

      {/* ─── Resolution priority hint ─── */}
      <div className="text-[10.5px] text-[#5C6B64] leading-relaxed bg-[#F9F9F8] border border-dashed border-[#E2E8DF] rounded-md p-3">
        <span className="font-medium text-[#1A2421]">Resolution priority:</span>{' '}
        Per-tenant override (this setting) → <code className="text-[10px]">CX_PUBLIC_BASE_URL</code> env →
        {' '}<code className="text-[10px]">PUBLIC_BASE_URL</code> env → preview default.
        Setting a value here is the strongest override and takes effect immediately for every QR.
      </div>
    </div>
  );
}


// iter171 — Landing-page customization card inside Settings tab.
//   • Lets the admin set brand name, welcome title/subtitle, thank-you
//     title/message, and platform-attribution toggle.
//   • Save round-trips to PUT /api/cx/settings/landing.
//   • Includes a live preview row + "Test scan ↗" of the first area so
//     the admin can verify their copy on the actual guest page.
function LandingPageCard({ headers, previewUrl }) {
  const [data, setData] = useState({
    cx_brand_name: '',
    cx_welcome_title: '',
    cx_welcome_subtitle: '',
    cx_thank_you_title: '',
    cx_thank_you_message: '',
    cx_show_platform_branding: false,
    cx_primary_color: 'var(--color-primary)',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/cx/settings/landing`, { headers })
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [headers]);

  const update = (k, v) => setData((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/cx/settings/landing`, data, { headers });
      setData(r.data);
      toast.success('Landing page saved · changes are live for guests');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Card className="border-[#E2E8DF]" data-testid="cx-landing-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-[15px]">Public landing page</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-[12.5px] text-[#5C6B64]">
          Customise what guests see when they scan a QR code. All fields are
          optional — defaults work for most properties.
        </p>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[12px]">Brand name shown to guests</Label>
            <Input value={data.cx_brand_name || ''}
                   onChange={(e) => update('cx_brand_name', e.target.value)}
                   placeholder="e.g. The Grand Hotel"
                   data-testid="cx-landing-brand-name" />
          </div>
          <div>
            <Label className="text-[12px]">Welcome title</Label>
            <Input value={data.cx_welcome_title || ''}
                   onChange={(e) => update('cx_welcome_title', e.target.value)}
                   placeholder="Share your experience"
                   data-testid="cx-landing-welcome-title" />
          </div>
        </div>

        <div>
          <Label className="text-[12px]">Welcome subtitle</Label>
          <Textarea rows={2} value={data.cx_welcome_subtitle || ''}
                    onChange={(e) => update('cx_welcome_subtitle', e.target.value)}
                    placeholder="Share your experience — your pulse shapes our service."
                    data-testid="cx-landing-welcome-subtitle" />
        </div>

        {/* iter173 — Primary brand color (hero gradient + submit button + accents) */}
        <div className="grid sm:grid-cols-[auto_1fr] gap-3 items-end pt-1">
          <div>
            <Label className="text-[12px]">Primary brand color</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={data.cx_primary_color || 'var(--color-primary)'}
                onChange={(e) => update('cx_primary_color', e.target.value)}
                className="w-10 h-10 rounded border border-[#E2E8DF] cursor-pointer"
                data-testid="cx-landing-color-input" />
              <Input
                value={data.cx_primary_color || 'var(--color-primary)'}
                onChange={(e) => update('cx_primary_color', e.target.value)}
                placeholder="var(--color-primary)"
                className="w-28 font-mono text-[12px] h-10"
                data-testid="cx-landing-color-hex" />
            </div>
          </div>
          <div>
            <Label className="text-[12px]">Live preview</Label>
            <div className="h-10 rounded-md flex items-center px-3 text-white text-[12.5px] font-medium shadow-sm"
                 style={{ background: data.cx_primary_color || 'var(--color-primary)' }}
                 data-testid="cx-landing-color-preview">
              Share your pulse
            </div>
          </div>
        </div>

        <div className="border-t border-dashed border-[#E2E8DF] pt-3 grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[12px]">Thank-you title</Label>
            <Input value={data.cx_thank_you_title || ''}
                   onChange={(e) => update('cx_thank_you_title', e.target.value)}
                   placeholder="Thank you!"
                   data-testid="cx-landing-ty-title" />
          </div>
          <div>
            <Label className="text-[12px]">Thank-you message</Label>
            <Textarea rows={2} value={data.cx_thank_you_message || ''}
                      onChange={(e) => update('cx_thank_you_message', e.target.value)}
                      placeholder="We've received your pulse. We genuinely appreciate you taking the time."
                      data-testid="cx-landing-ty-message" />
          </div>
        </div>

        <label className="flex items-center gap-2 text-[12.5px] text-[#1A2421] cursor-pointer pt-1">
          <input type="checkbox" checked={!!data.cx_show_platform_branding}
                 onChange={(e) => update('cx_show_platform_branding', e.target.checked)}
                 data-testid="cx-landing-show-platform" />
          Show "Powered by …" platform attribution on guest page <span className="text-[10.5px] text-[#5C6B64]">(off by default — guest sees only your brand)</span>
        </label>

        <div className="flex items-center gap-2 pt-2 flex-wrap">
          <Button onClick={save} disabled={saving}
                  className="bg-[color:var(--color-primary)] hover:bg-[#0B2A22] text-white"
                  data-testid="cx-landing-save">
            {saving ? 'Saving…' : 'Save landing page'}
          </Button>
          {previewUrl && (
            <a href={previewUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" data-testid="cx-landing-preview-btn">
                Preview public page ↗
              </Button>
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

